// client/src/components/public/SocialProofStrip.jsx
//
// Badges + counts on the public event page (Growth Phase 8). Renders only
// what the server returns, and the server returns only what the event's
// social-proof switches allow (services/socialProof.js). "Who's going"
// people are buyers who opted in at checkout: first name + initial and an
// optional member photo, never more.

import React from 'react';
import { useTranslation } from 'react-i18next';

export default function SocialProofStrip({ proof }) {
  const { t } = useTranslation();
  if (!proof || !proof.enabled) return null;

  const badges = [];
  if (proof.soldOut) badges.push({ key: 'soldOut', text: t('socialProof.soldOut', 'Sold out'), tone: 'warn' });
  else if (proof.spotsLeft != null) badges.push({ key: 'spots', text: t('socialProof.spotsLeft', '{{count}} spots left', { count: proof.spotsLeft }), tone: 'warn' });
  if (proof.sellingFast) badges.push({ key: 'fast', text: t('socialProof.sellingFast', 'Selling fast'), tone: 'hot' });
  if (proof.going > 0) badges.push({ key: 'going', text: t('socialProof.going', '{{count}} going', { count: proof.going }), tone: 'info' });
  if (proof.entries > 0) badges.push({ key: 'entries', text: t('socialProof.entries', '{{count}} entries', { count: proof.entries }), tone: 'info' });

  const people = proof.attendees?.people || [];
  const more = Math.max(0, (proof.attendees?.total || 0) - people.length);
  if (!badges.length && !people.length) return null;

  return (
    <div className="os-proof" style={{ display: 'grid', gap: 10, margin: '12px 0 4px' }}>
      {badges.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {badges.map((b) => <span key={b.key} className={`os-proof-badge ${b.tone}`} style={badge[b.tone]}>{b.text}</span>)}
        </div>
      )}
      {people.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="os-muted" style={{ fontSize: 13, fontWeight: 600 }}>{t('socialProof.whosGoing', "Who's going")}</span>
          <ul aria-label={t('socialProof.whosGoing', "Who's going")} style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0 }}>
            {people.map((p, i) => (
              <li key={`${p.name}-${i}`} title={p.name} style={{ marginLeft: i ? -8 : 0 }}>
                {p.avatar
                  ? <img src={p.avatar} alt={p.name} style={avatar} />
                  : <span className="os-proof-initials" role="img" aria-label={p.name} style={{ ...avatar, ...initials }}>{p.initials}</span>}
              </li>
            ))}
          </ul>
          {more > 0 && <span className="os-muted" style={{ fontSize: 13 }}>{t('socialProof.andMore', '+{{count}} more', { count: more })}</span>}
        </div>
      )}
    </div>
  );
}

const pill = { fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 999 };
const badge = {
  warn: { ...pill, background: '#fef3c7', color: '#92400e' },
  hot: { ...pill, background: '#fee2e2', color: '#991b1b' },
  info: { ...pill, background: '#e0e7ff', color: '#1e3a8a' },
};
const avatar = { width: 32, height: 32, borderRadius: '50%', border: '2px solid #ffffff', objectFit: 'cover', display: 'inline-flex' };
const initials = { alignItems: 'center', justifyContent: 'center', background: '#253866', color: '#ffffff', fontSize: 12, fontWeight: 700 };
